import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { FlaskConical } from 'lucide-react';
import { useCreateSelfTest, useSelfTests } from '../../hooks/useLongevityAssessments';
import { CATEGORY_LABELS } from './categoryLabels';
import type { SelfTest } from '../../api/longevityAssessmentApi';

/**
 * The advisor's own test runs (Feature 118).
 *
 * A block of its own rather than rows in the client list, and above it, because
 * these are not clients: they are excluded from Sent, Completed and average
 * readiness, so showing them as client cards would contradict the numbers
 * directly above them.
 *
 * It renders for an advisor who has sent nothing, which is the point. The section
 * around it used to disappear entirely until the first send, and an advisor with
 * no clients yet is exactly the one who wants to see what they would be sending.
 */

/**
 * Starts a test run. It sits at the top of the Longevity Assessments section,
 * apart from the list of runs below, so it is the first thing an advisor sees.
 * Always MVP 2 — the only version the send dialog sends, so an advisor tries
 * what their clients would get.
 */
export function TrySelfTestButton() {
  const navigate = useNavigate();
  const createSelfTest = useCreateSelfTest();

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        variant="outline"
        size="sm"
        // Straight into the questions on success: the advisor asked to try the
        // assessment, so landing them on a list with a new row to find would be
        // a step they did not ask for.
        onClick={() =>
          createSelfTest.mutate('2', {
            onSuccess: (run) => navigate(`/longevity-assessment/${run.linkToken}`),
          })
        }
        // Disabled while in flight — two runs started by a double click are
        // allowed by the database but are not what the advisor meant.
        disabled={createSelfTest.isPending}
      >
        {createSelfTest.isPending ? 'Starting…' : 'Try Assessment'}
      </Button>
      {createSelfTest.isError && (
        <p className="text-sm text-red-600">Couldn&apos;t start a test run. Please try again.</p>
      )}
    </div>
  );
}

function runLabel(run: SelfTest): string {
  if (run.status === 'COMPLETED') return 'Completed';
  if (run.status === 'IN_PROGRESS') return 'In progress';
  return 'Not started';
}

function RunRow({ run }: { run: SelfTest }) {
  const navigate = useNavigate();
  const isV2 = run.questionSetVersion === '2';
  const started = new Date(run.createdAt).toLocaleDateString();

  return (
    <div className="flex items-center justify-between rounded-lg border border-dashed border-gray-300 bg-white p-4">
      <div>
        <div className="font-medium text-gray-900">
          Test run
          <span className="ml-2 text-sm font-normal text-gray-500">started {started}</span>
        </div>
        <Badge variant="secondary" className="mt-1">
          {runLabel(run)}
        </Badge>
      </div>

      <div className="flex items-center gap-2 text-right">
        {run.status === 'COMPLETED' ? (
          <>
            {/* Labelled, like the client card. A bare number next to two buttons
                reads as a count of something rather than a readiness score, which
                is exactly how it was first read. */}
            {run.overallScore != null && (
              <div className="mr-1 leading-tight">
                <div className="font-serif text-2xl text-[#026857]">{run.overallScore}</div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">
                  {run.category ? CATEGORY_LABELS[run.category] : 'Readiness'}
                </div>
              </div>
            )}
            {/* Both views, unlike a client card which picks one. Seeing what the
                client gets AND what lands on the advisor's desk is the reason for
                a test run, and on a self-test both are open regardless of the
                share choice — there is no client whose privacy it protects. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/longevity-assessment/results/${run.id}`)}
            >
              Client report
            </Button>
            {isV2 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(`/longevity-assessment/advisor-report/${run.id}`)}
              >
                Advisor report
              </Button>
            )}
          </>
        ) : (
          // The same public take flow a client opens, by the same token — a test
          // run that went through a separate screen would stop being a preview.
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(`/longevity-assessment/${run.linkToken}`)}
          >
            {run.status === 'IN_PROGRESS' ? 'Continue' : 'Start'}
          </Button>
        )}
      </div>
    </div>
  );
}

export function SelfTestRuns() {
  const { data: runs, isLoading, isError } = useSelfTests();

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <FlaskConical className="h-3.5 w-3.5 text-gray-400" aria-hidden />
        <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Your test runs
        </h5>
      </div>

      {/*
        Only say "you have none" when that is what the server said. Reading the
        query's data alone, a failed or in-flight request also leaves `runs`
        undefined — so an advisor whose list failed to load was told they had
        never taken one, with their runs still there. The section above this one
        has always distinguished the three; this did not.
      */}
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading your test runs…</p>
      ) : isError ? (
        // The start button at the top of the section stays: starting a run does
        // not depend on the list, and it is the one thing still worth offering
        // when the list is missing.
        <p className="text-sm text-red-600">
          Couldn&apos;t load your test runs. Any you have already taken are still there.
        </p>
      ) : runs?.length ? (
        runs.map((run) => <RunRow key={run.id} run={run} />)
      ) : (
        <p className="text-sm text-gray-500">
          Take the assessment yourself to see exactly what your clients see. Test runs stay out of
          your stats and client list, and you can take it as many times as you like.
        </p>
      )}
    </div>
  );
}
